import { ActionPermissions, CustomPermission, RoutesEnum, ServiceStatusCode } from '../@types';
import {
  MainMenuItem,
  SubMenuItem,
  ChildMenuItem,
} from '../components/molecules/Drawer/Drawer.props';

export type PermissionInput = string | CustomPermission;

export interface RouteConfig {
  path?: string;
  element?: React.ReactNode;
  children: { path: string; element?: React.ReactNode }[];
}

export const ROOT_NODE = {
  id: 1000,
  name: 'root',
  code: 'root',
  isGranted: false,
  children: [],
};

// get the layouts according to user permissions
export const permittedLayouts = (
  menuData: MainMenuItem[],
  permissions: PermissionInput[] | null | undefined,
  options?: { isClientCustomer?: boolean }
): MainMenuItem[] => {
  // Extract permission code strings (handles both string[] and CustomPermission[])
  const permissionCodes: string[] =
    permissions?.map((p: PermissionInput) => (typeof p === 'string' ? p : p.code)) ?? [];

  // Split granted user permission code by '.' and then get the first keyword.
  // ex-: input => service.create | output => ['service']
  const activePermissionParentCodes: string[] =
    permissionCodes.length > 0
      ? [
          ...new Set(
            permissionCodes.map((filteredPermission: string) => filteredPermission.split('.')[0])
          ),
        ]
      : [];

  // get the all permission codes only
  const activePermissionNestedChildCodes: string[] =
    permissionCodes.length > 0
      ? [
          ...new Set(
            permissionCodes
              .filter((permission: string) => permission.split('.').length > 2)
              .map(
                (filteredPermission: string) =>
                  `${filteredPermission.split('.')[0]}.${filteredPermission.split('.')[1]}.${filteredPermission.split('.')[2]}`
              )
          ),
        ]
      : [];

  // Menu items without a `permission` field are always visible, so that new
  // sections (e.g. Tools) can ship before their permissions are wired up on
  // the backend. Home is also permission-less but is prepended later via
  // `actionableMenus.unshift(home)` — exclude it here so it isn't duplicated.
  const filteredParentMenus = menuData.filter((menuItem: MainMenuItem) => {
    if (menuItem.id === 'home') return false;
    if (!menuItem.permission) return true;
    return activePermissionParentCodes.some(
      (activePermissionParentCode: string) => menuItem.permission === activePermissionParentCode
    );
  });

  // Match a sub-item's permission against the user's permissions hierarchically:
  // a sub-item with `tools.import` is visible if the user has any `tools.import.*`,
  // and a sub-item with `tools.amtek_analytics.general` is visible only if the
  // user has that exact code (or something more specific). This mirrors
  // `isUserHasPermission` so menu visibility and page-level gates stay aligned
  // even when sub-items use 3-segment permissions.
  const filteredChildMenus: MainMenuItem[] = filteredParentMenus.map(
    (filteredParentMenu: MainMenuItem) => ({
      ...filteredParentMenu,
      ...(filteredParentMenu.subMenuItems && {
        subMenuItems: filteredParentMenu.subMenuItems?.filter((subMenuItem: SubMenuItem) => {
          if (!subMenuItem.permission) return true;
          const menuPerm = subMenuItem.permission;
          return permissionCodes.some(
            (code: string) => code === menuPerm || code.startsWith(`${menuPerm}.`)
          );
        }),
      }),
    })
  );

  const filteredNestedChildMenus: MainMenuItem[] = filteredChildMenus.map(
    (filteredParentMenu: MainMenuItem) => ({
      ...filteredParentMenu,
      ...(filteredParentMenu.subMenuItems && {
        subMenuItems: filteredParentMenu.subMenuItems?.map((nestedsubMenuItems: SubMenuItem) => ({
          ...nestedsubMenuItems,
          ...(nestedsubMenuItems.subMenuItems && {
            subMenuItems: nestedsubMenuItems.subMenuItems?.filter(
              (nestedsubMenuItem: ChildMenuItem) => {
                if (!nestedsubMenuItem.permission) return true;
                return activePermissionNestedChildCodes.some(
                  (activePermissionNestedChildCode: string) =>
                    nestedsubMenuItem.permission === activePermissionNestedChildCode
                );
              }
            ),
          }),
        })),
      }),
    })
  );

  // Drop menu nodes that would render as dead buttons. A node is "dead" when
  // it has no URL of its own AND no visible sub-items — typically because the
  // user has a permission in a namespace (e.g. `part.export`) that doesn't
  // correspond to any navigable menu route, so the permission-parent check
  // surfaces the section header but the sub-item filter removes everything
  // inside it. Prune sub-menus first so their emptiness can cascade up.
  const actionableMenus: MainMenuItem[] = filteredNestedChildMenus
    .map(
      (parent: MainMenuItem): MainMenuItem => ({
        ...parent,
        ...(parent.subMenuItems && {
          subMenuItems: parent.subMenuItems.filter(
            (sub: SubMenuItem) => !!sub.url || !!sub.subMenuItems?.length
          ),
        }),
      })
    )
    .filter((parent: MainMenuItem) => !!parent.url || !!parent.subMenuItems?.length);

  // Client-customer users land on /services as their homepage and never see a
  // dedicated Home dashboard, so don't surface the Home entry in their nav.
  if (!options?.isClientCustomer) {
    const home: MainMenuItem = { id: 'home', name: 'Home', url: '/' };
    actionableMenus.unshift(home);
  }

  return actionableMenus;
};

// get the routes according to user permissions
export const permittedRoutes = (layoutes: MainMenuItem[], routes: RouteConfig): RouteConfig => {
  const layoutUrls: string[] = [];
  let url: string;

  // map filtered layoutes which is returned from const permittedLayouts
  layoutes.map((parent: MainMenuItem) => {
    // push urls from parent level
    parent.url?.length && layoutUrls.push(parent.url);

    // push urls from child level
    parent.subMenuItems?.map((child: SubMenuItem) => {
      if (child.url.split('/').includes('create')) {
        url = child.url.replace('create', ':id');
        layoutUrls.push(url);
        // Dedicated create pages (e.g. /structure/create, /shipments/stock/create)
        // are registered as their own static route, so the literal create path
        // must also be whitelisted. Without it the route is filtered out and the
        // URL falls through to the `/:id` edit route, loading the legacy editor
        // with id="create" (which fires GET /nodes/create/subtree → 400).
        layoutUrls.push(child.url);
      } else if (child.url.split('/').includes('add')) {
        url = child.url.replace('add', ':id');
        layoutUrls.push(url);
      } else {
        layoutUrls.push(child.url);
      }

      // push urls from nested child level
      child.subMenuItems?.map((nestedChild: ChildMenuItem) => {
        if (nestedChild.url.split('/').includes('create')) {
          url = nestedChild.url.replace('create', ':id');
          layoutUrls.push(url);
          // Whitelist the literal create path too — see the child-level note above.
          layoutUrls.push(nestedChild.url);
        } else if (nestedChild.url.split('/').includes('add')) {
          url = nestedChild.url.replace('add', ':id');
          layoutUrls.push(url);
        } else {
          layoutUrls.push(nestedChild.url);
        }
      });
    });
  });

  // push urls that not included in data.js
  layoutUrls.map((layoutUrl: string) => {
    if (layoutUrl === RoutesEnum.UserCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.UserCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.RoleCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.RoleCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.StructureTypeCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.StructureTypeCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.ClientAndContractCreateAndUpdateRoute) {
      layoutUrls.push('/configure/clients/:type/:id');
    }
    if (layoutUrl === RoutesEnum.ServiceTypeCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.ServiceTypeCreateAndUpdateRoute}/:code`);
    }
    if (layoutUrl === RoutesEnum.ServiceStatusCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.ServiceStatusCreateAndUpdateRoute}/:code`);
    }
    if (layoutUrl === RoutesEnum.PartTypeCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.PartTypeCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.PartsSearchRoute) {
      layoutUrls.push(`${RoutesEnum.PartUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.ShipmentUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.ShipmentUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.BatchReceiveCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.BatchReceiveCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.TLCTemplateCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.TLCTemplateCreateAndUpdateRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.ReceivedShipmentRoute) {
      layoutUrls.push(`${RoutesEnum.ReceivedShipmentRoute}/:id`);
    }
    if (layoutUrl === RoutesEnum.ServiceCreateAndUpdateRoute) {
      layoutUrls.push(`${RoutesEnum.ServiceCreateAndUpdateRoute}/:id`);
    }
    // Pick task detail route is dynamic and not a menu URL - whitelist it when
    // the /picktasks list is permitted (same pattern as shipments/services).
    if (layoutUrl === '/picktasks') {
      layoutUrls.push('/picktasks/:id');
    }

    layoutUrls.push(`${RoutesEnum.StructureCreateAndUpdateRoute}/:id`);
    layoutUrls.push(RoutesEnum.LoaderRoute);
    layoutUrls.push(RoutesEnum.ProfileRoute);
  });

  // compare filtered urls against route urls and get common
  const filteredRoutes: RouteConfig = {
    ...routes,
    children: routes.children.filter((route: { path: string }) => {
      return layoutUrls.some((layoutUrl: string) => layoutUrl == route.path);
    }),
  };

  return filteredRoutes;
};

export const isUserHasPermission = (
  actionPermission: string,
  systemPermissions: PermissionInput[] | null | undefined
): boolean => {
  // Extract permission code strings (handles both string[] and CustomPermission[])
  const permissionCodes: string[] =
    systemPermissions?.map((p: PermissionInput) => (typeof p === 'string' ? p : p.code)) ?? [];

  const systemActionPermissions: string[] = [
    ...new Set(
      permissionCodes
        .filter((permission: string) => permission.split('.').length > 1)
        .map((filteredPermission: string) => filteredPermission)
    ),
  ];

  // Use exact match or hierarchical check (user has more specific permission than required)
  // e.g., if user has 'service.edit.actions', they can perform 'service.edit' operations
  return systemActionPermissions.some(
    (systemActionPermission: string) =>
      systemActionPermission === actionPermission ||
      systemActionPermission.startsWith(actionPermission + '.')
  );
};

export const isUserHasPartActionPermissions = (
  permissions: PermissionInput[] | null | undefined,
  isCheckIn: boolean,
  serviceStatusCode: string
): boolean => {
  const isHaveAnyTimePermission = isUserHasPermission(
    ActionPermissions.Service_Edit_Actions_Part_Action_Anytime,
    permissions
  );
  const isHaveInProgressPermission = isUserHasPermission(
    ActionPermissions.Service_Edit_Actions_Part_Action_Inprogress,
    permissions
  );

  if (isHaveAnyTimePermission) {
    return true;
  }

  if (isHaveInProgressPermission) {
    const isOnSite = serviceStatusCode === ServiceStatusCode.OnSite;

    if (isOnSite || isCheckIn) {
      return true;
    }
  }

  return false;
};
